import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import {
  generatePath,
  useHistory,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import { getRouteWithoutPath } from "utils/routes";
import { invert } from "lodash";
import { useEffect } from "react";

interface useTabsWithUrlProps<TabType extends string | number = string> {
  tabToDisplayName: Record<TabType, string>;
  tabs?: TabType[];
  path?: string;
  getPathFromTab?: (tab: TabType, baseRoute: string) => string | undefined;
  defaultTab?: TabType;
  keepQuery?: boolean;
  skipUrlInitialization?: boolean;
}

const useTabsWithUrl = <TabType extends string | number = string>({
  tabToDisplayName,
  tabs,
  getPathFromTab,
  path,
  defaultTab,
  keepQuery = true,
  skipUrlInitialization,
}: useTabsWithUrlProps<TabType>) => {
  const location = useLocation();
  const history = useHistory();
  const { historyReplace } = useHistoryWithQuery();
  const match = useRouteMatch(path || "");
  let { tab: urlTab } = (match?.params || {}) as {
    tab?: TabType;
  };
  urlTab = tabs?.find((tab) => tab === urlTab);
  const displayNameToTab = invert(tabToDisplayName);

  const onChange = (tabDisplayName: string) => {
    if (!path) return;
    const tab = displayNameToTab[tabDisplayName] as TabType;
    let pathWithTab: string | undefined;
    if (path && getPathFromTab) {
      const baseRoute = getRouteWithoutPath(location.pathname, path);
      pathWithTab = getPathFromTab(tab, baseRoute);
    } else {
      pathWithTab = generatePath(path, { tab });
    }
    if (pathWithTab) {
      (keepQuery ? historyReplace : history.replace)(pathWithTab);
    }
  };

  useEffect(() => {
    if (!urlTab && defaultTab && !skipUrlInitialization) {
      onChange(tabToDisplayName[defaultTab]);
    }
  }, [urlTab, defaultTab, location.pathname, skipUrlInitialization]);

  const tab = urlTab ?? defaultTab;

  return {
    tabDisplayName: tab ? tabToDisplayName[tab] : undefined,
    tab,
    onChange,
  };
};

export default useTabsWithUrl;
